import { useEffect, useRef, useState } from "react";
import { Play, RefreshCw, Search, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import { findProfilesNeedingBackfill, runBackfill } from "@/lib/locationBackfill";

/**
 * /admin/backfill-locations — admin-only one-time utility.
 *
 * Mounted under <AdminRoute><AdminShell> so it inherits the admin guard,
 * the chunky tab nav, and the brand chrome.
 */
export default function AdminBackfill() {
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const logRef = useRef(null);

  const scan = async () => {
    setScanning(true);
    setSummary(null);
    try {
      const r = await findProfilesNeedingBackfill();
      setRows(r);
      setLog([`Found ${r.length} profile(s) needing backfill.`]);
    } catch (err) {
      toast.error(err?.message || "Scan failed.");
      setRows([]);
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => { scan(); }, []);

  // Auto-scroll log to bottom on append.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log.length]);

  const appendLog = (line) =>
    setLog((arr) => (arr.length > 500 ? [...arr.slice(-400), line] : [...arr, line]));

  const run = async () => {
    if (!rows.length) {
      toast.message("Nothing to backfill.");
      return;
    }
    setRunning(true);
    setProgress({ done: 0, total: rows.length });
    setSummary(null);
    setLog([`${dryRun ? "[dry-run]" : "[execute]"} starting backfill of ${rows.length} profile(s)…`]);
    try {
      const s = await runBackfill({
        rows,
        dryRun,
        onProgress: (p) => setProgress({ done: p.done, total: p.total }),
        onLog: appendLog,
        throttleMs: 250,
        batchSize: 8,
      });
      setSummary(s);
      toast.success(`${dryRun ? "Dry-run" : "Backfill"} complete.`);
      // Re-scan after a real run so the table reflects what's left.
      if (!dryRun) await scan();
    } catch (err) {
      toast.error(err?.message || "Backfill failed.");
      appendLog(`!! ${err?.message || "unknown error"}`);
    } finally {
      setRunning(false);
    }
  };

  const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-ink/55">Admin · Utility</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 leading-none">
            Location backfill
          </h1>
          <p className="text-sm text-ink/65 mt-2 max-w-2xl">
            One-time job. For pre-v8 users who have a <span className="font-extrabold">city</span> but no
            coordinates, this resolves <span className="font-extrabold">lat/lng + state + country</span> via
            Mapbox and patches their profile. Safe to re-run.
          </p>
        </div>
        <button
          type="button"
          onClick={scan}
          disabled={scanning || running}
          data-testid="backfill-scan"
          className="inline-flex items-center gap-1.5 border-[2.5px] border-ink rounded-full px-4 py-2 text-[11px] uppercase tracking-widest font-extrabold bg-paper hover:bg-ink hover:text-paper transition-colors disabled:opacity-60"
        >
          <RefreshCw size={13} strokeWidth={2.6} className={scanning ? "animate-spin" : ""} />
          {scanning ? "Scanning…" : "Rescan"}
        </button>
      </header>

      {/* Controls */}
      <div className="border-[2.5px] border-ink rounded-3xl p-5 bg-paper flex flex-wrap items-center gap-4">
        <div className="inline-flex gap-1 p-1 border-[2.5px] border-ink rounded-full">
          <Toggle active={dryRun}  onClick={() => setDryRun(true)}  testid="backfill-mode-dry">Dry-run</Toggle>
          <Toggle active={!dryRun} onClick={() => setDryRun(false)} testid="backfill-mode-execute">Execute</Toggle>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running || scanning || rows.length === 0}
          data-testid="backfill-run"
          className="inline-flex items-center gap-1.5 bg-neon border-[2.5px] border-ink rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-extrabold shadow-pop-sm disabled:opacity-50 hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform"
        >
          <Play size={13} strokeWidth={2.6} /> Run {dryRun ? "dry-run" : "backfill"}
        </button>
        <span className="text-xs text-ink/60">
          {rows.length} profile{rows.length === 1 ? "" : "s"} need attention.
        </span>
        {!dryRun && (
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-destructive font-extrabold">
            <AlertTriangle size={12} strokeWidth={2.6} /> Live writes enabled
          </span>
        )}
      </div>

      {/* Progress */}
      {(running || progress.done > 0) && (
        <div data-testid="backfill-progress" className="border-[2.5px] border-ink rounded-3xl p-5 bg-paper">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs uppercase tracking-widest font-extrabold">Progress</span>
            <span className="text-xs tabular-nums">{progress.done} / {progress.total}  ({percent}%)</span>
          </div>
          <div className="h-3 rounded-full bg-ink/10 border border-ink/30 overflow-hidden">
            <div className="h-full bg-neon transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div data-testid="backfill-summary" className="border-[2.5px] border-ink rounded-3xl p-5 bg-paper grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <Stat label="Total"      value={summary.total}    />
          <Stat label="Resolved"   value={summary.resolved} />
          <Stat label="Written"    value={summary.written}  tone="neon" />
          <Stat label="Skipped"    value={summary.skipped}  />
          <Stat label="Failed"     value={summary.failed}   tone={summary.failed > 0 ? "alert" : ""} />
        </div>
      )}

      {/* Log */}
      <div ref={logRef} className="border-[2.5px] border-ink rounded-3xl p-4 bg-ink text-paper font-mono text-[12px] leading-relaxed h-72 overflow-y-auto">
        {log.length === 0 ? <span className="text-paper/50">Idle.</span> :
          log.map((line, i) => (
            <div key={i} className={
              line.startsWith("✓") ? "text-neon" :
              line.startsWith("✗") || line.startsWith("!!") ? "text-destructive" :
              line.startsWith("?") ? "text-paper/55" : ""
            }>{line}</div>
          ))
        }
      </div>

      {/* Preview table — first 30 rows so admin can spot-check */}
      {!running && rows.length > 0 && (
        <details className="border-[2.5px] border-ink rounded-3xl bg-paper">
          <summary className="cursor-pointer px-5 py-4 text-xs uppercase tracking-widest font-extrabold inline-flex items-center gap-2 select-none">
            <Search size={13} strokeWidth={2.6} /> Preview rows ({rows.length})
          </summary>
          <div className="overflow-x-auto border-t border-ink/15">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-ink/55">
                <tr>
                  <th className="px-4 py-2">@username</th>
                  <th className="px-4 py-2">city</th>
                  <th className="px-4 py-2">state</th>
                  <th className="px-4 py-2">country</th>
                  <th className="px-4 py-2">lat / lng</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 30).map((r) => (
                  <tr key={r.id} className="border-t border-ink/10">
                    <td className="px-4 py-2 font-bold">@{r.username || "—"}</td>
                    <td className="px-4 py-2">{r.city}</td>
                    <td className="px-4 py-2 text-ink/65">{r.state || "—"}</td>
                    <td className="px-4 py-2 text-ink/65">{r.country || "—"}</td>
                    <td className="px-4 py-2 text-ink/65 tabular-nums">
                      {r.location_lat != null && r.location_lng != null
                        ? `${r.location_lat.toFixed(3)}, ${r.location_lng.toFixed(3)}`
                        : <span className="inline-flex items-center gap-1 text-destructive"><AlertTriangle size={12} strokeWidth={2.6} /> missing</span>}
                    </td>
                  </tr>
                ))}
                {rows.length > 30 && (
                  <tr><td colSpan={5} className="px-4 py-3 text-center text-ink/55 text-xs uppercase tracking-widest">+ {rows.length - 30} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {!scanning && rows.length === 0 && (
        <div data-testid="backfill-empty" className="border-[2.5px] border-ink rounded-3xl p-8 bg-paper text-center">
          <Check size={28} strokeWidth={2.6} className="mx-auto text-neon mb-2" />
          <p className="font-display text-2xl font-bold tracking-tight">All profiles backfilled.</p>
          <p className="text-sm text-ink/60 mt-1">Nothing to do here — every active profile already has location data.</p>
        </div>
      )}
    </div>
  );
}

function Toggle({ active, onClick, testid, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-extrabold rounded-full transition-colors ${active ? "bg-ink text-paper" : "text-ink/65 hover:text-ink"}`}>
      {children}
    </button>
  );
}

function Stat({ label, value, tone }) {
  const bg = tone === "neon" ? "bg-neon" : tone === "alert" ? "bg-destructive/15 text-destructive" : "bg-paper";
  return (
    <div className={`border-[2px] border-ink rounded-2xl px-3 py-3 ${bg}`}>
      <div className="text-2xl font-display font-extrabold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
